import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
    carouselPlayerControlBarSx,
    carouselPlayerControlBtnSx,
    carouselPlayerSliderSx,
    carouselPlayerVolumeLabelSx,
    carouselFooterTitleSx,
} from '../assetExplorerTheme';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

export interface ThemedCarouselVideoProps {
    src: string;
    mediaKey: string;
}

/** Paused by default; themed Play / Pause / Stop / volume / mute / fullscreen */
export const ThemedCarouselVideo: React.FC<ThemedCarouselVideoProps> = ({ src, mediaKey }) => {
    const theme = useTheme();
    const videoRef = useRef<HTMLVideoElement>(null);
    const fsWrapRef = useRef<HTMLDivElement>(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        v.currentTime = 0;
        setPlaying(false);
        setVolume(1);
        setMuted(false);
        v.volume = 1;
        v.muted = false;
    }, [src, mediaKey]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.volume = volume;
    }, [volume]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = muted;
    }, [muted]);

    useEffect(() => {
        const onFs = () => {
            const el = fsWrapRef.current;
            setIsFullscreen(Boolean(el && document.fullscreenElement === el));
        };
        document.addEventListener('fullscreenchange', onFs);
        return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);

    useEffect(() => {
        return () => {
            const el = fsWrapRef.current;
            if (el && document.fullscreenElement === el) {
                document.exitFullscreen().catch(() => undefined);
            }
        };
    }, [mediaKey]);

    const handlePlay = useCallback(() => {
        videoRef.current?.play().catch(() => undefined);
    }, []);

    const handlePause = useCallback(() => {
        videoRef.current?.pause();
    }, []);

    const handleStop = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        v.currentTime = 0;
        setPlaying(false);
    }, []);

    const toggleMute = useCallback(() => {
        setMuted((m) => !m);
    }, []);

    const toggleFullscreen = useCallback(async () => {
        const el = fsWrapRef.current;
        if (!el) return;
        try {
            if (!document.fullscreenElement) {
                await el.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch {
            /* ignore */
        }
    }, []);

    const onVolumeSliderChange = (_: Event, value: number | number[]) => {
        const vRaw = Array.isArray(value) ? value[0] : value;
        const nv = Math.min(1, Math.max(0, vRaw / 100));
        setVolume(nv);
        if (nv > 0 && muted) {
            setMuted(false);
        }
    };

    return (
        <Box
            ref={fsWrapRef}
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 1200 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    bgcolor: '#000',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `2px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxHeight: 'calc(100vh - 240px)',
                        '& video': {
                            width: '100%',
                            height: 'auto',
                            maxHeight: 'calc(100vh - 240px)',
                            display: 'block',
                        },
                    }}
                >
                    <video
                        ref={videoRef}
                        src={src}
                        preload="metadata"
                        playsInline
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => setPlaying(false)}
                    />
                </Box>
            </Box>

            <Stack direction="row" sx={carouselPlayerControlBarSx(theme)}>
                <Tooltip title="Play">
                    <span>
                        <IconButton
                            aria-label="Play"
                            onClick={handlePlay}
                            disabled={playing}
                            sx={carouselPlayerControlBtnSx(theme)}
                            size="small"
                        >
                            <PlayArrowIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Pause">
                    <span>
                        <IconButton
                            aria-label="Pause"
                            onClick={handlePause}
                            disabled={!playing}
                            sx={carouselPlayerControlBtnSx(theme)}
                            size="small"
                        >
                            <PauseIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Stop">
                    <IconButton aria-label="Stop" onClick={handleStop} sx={carouselPlayerControlBtnSx(theme)} size="small">
                        <StopIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ width: 8 }} />

                <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                    <IconButton aria-label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute} sx={carouselPlayerControlBtnSx(theme)} size="small">
                        {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                </Tooltip>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ ...carouselPlayerVolumeLabelSx(theme), display: { xs: 'none', sm: 'block' } }}>
                        Volume
                    </Typography>
                    <Slider
                        size="small"
                        value={muted ? 0 : volume * 100}
                        onChange={(_, v) => {
                            const val = Array.isArray(v) ? v[0] : v;
                            onVolumeSliderChange(_ as Event, val);
                            if (val > 0) setMuted(false);
                        }}
                        sx={carouselPlayerSliderSx(theme)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${Math.round(v)}%`}
                    />
                </Stack>

                <Box sx={{ width: 8 }} />

                <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                    <IconButton aria-label="Fullscreen" onClick={toggleFullscreen} sx={carouselPlayerControlBtnSx(theme)} size="small">
                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

export interface ThemedCarouselAudioProps {
    src: string;
    mediaKey: string;
    title: string;
}

/** Themed Play / Pause / Stop / volume / mute — native audio element without visible native chrome */
export const ThemedCarouselAudio: React.FC<ThemedCarouselAudioProps> = ({ src, mediaKey, title }) => {
    const theme = useTheme();
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [volume, setVolume] = useState(1);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
        setVolume(1);
        setMuted(false);
        a.volume = 1;
        a.muted = false;
    }, [src, mediaKey]);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        a.volume = volume;
    }, [volume]);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        a.muted = muted;
    }, [muted]);

    const handlePlay = useCallback(() => {
        audioRef.current?.play().catch(() => undefined);
    }, []);

    const handlePause = useCallback(() => {
        audioRef.current?.pause();
    }, []);

    const handleStop = useCallback(() => {
        const a = audioRef.current;
        if (!a) return;
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
    }, []);

    const toggleMute = useCallback(() => setMuted((m) => !m), []);

    const onVolumeSliderChange = (_: Event, value: number | number[]) => {
        const vRaw = Array.isArray(value) ? value[0] : value;
        const nv = Math.min(1, Math.max(0, vRaw / 100));
        setVolume(nv);
        if (nv > 0 && muted) setMuted(false);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: 560 }, px: { xs: 1, sm: 2 }, position: 'relative' }}>
            <Typography
                variant="subtitle1"
                sx={{ ...carouselFooterTitleSx(theme), mb: 2, textAlign: 'center' }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    opacity: 0,
                    overflow: 'hidden',
                    clipPath: 'inset(50%)',
                    pointerEvents: 'none',
                    bottom: 0,
                    right: 0,
                }}
            >
                <audio ref={audioRef} src={src} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
            </Box>

            <Stack direction="row" sx={carouselPlayerControlBarSx(theme)}>
                <Tooltip title="Play">
                    <span>
                        <IconButton aria-label="Play" onClick={handlePlay} disabled={playing} sx={carouselPlayerControlBtnSx(theme)} size="small">
                            <PlayArrowIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Pause">
                    <span>
                        <IconButton aria-label="Pause" onClick={handlePause} disabled={!playing} sx={carouselPlayerControlBtnSx(theme)} size="small">
                            <PauseIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Stop">
                    <IconButton aria-label="Stop" onClick={handleStop} sx={carouselPlayerControlBtnSx(theme)} size="small">
                        <StopIcon />
                    </IconButton>
                </Tooltip>

                <Box sx={{ width: 8 }} />

                <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                    <IconButton aria-label={muted ? 'Unmute' : 'Mute'} onClick={toggleMute} sx={carouselPlayerControlBtnSx(theme)} size="small">
                        {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                </Tooltip>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 140 }}>
                    <Typography variant="caption" sx={{ ...carouselPlayerVolumeLabelSx(theme), flexShrink: 0 }}>
                        Volume
                    </Typography>
                    <Slider
                        size="small"
                        value={muted ? 0 : volume * 100}
                        onChange={(_, v) => {
                            const val = Array.isArray(v) ? v[0] : v;
                            onVolumeSliderChange(_ as Event, val);
                            if (val > 0) setMuted(false);
                        }}
                        sx={{ ...carouselPlayerSliderSx(theme), flex: 1, maxWidth: 220 }}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${Math.round(v)}%`}
                    />
                </Stack>
            </Stack>
        </Box>
    );
};
